import type { Libraries } from '@react-google-maps/api';

/**
 * Single source of truth for the Google Maps JS libraries the app needs.
 *
 * `useJsApiLoader` is a global singleton — every call in the app must pass
 * the SAME options, including `id` and `libraries`, or later mounts can throw
 * "Loader must not be called again with different options."
 *
 * The list intentionally unions every library any surface in the app
 * needs. Adding one here costs ~few KB of JS — paying it once globally is
 * much cheaper than a broken-on-second-render Drawing Manager.
 */
export const GOOGLE_MAPS_LIBRARIES: Libraries = ['places', 'drawing'];

export const GOOGLE_MAPS_SCRIPT_ID = 'google-places-script' as const;

/**
 * Google removed `google.maps.drawing.DrawingManager` in Maps JS API v3.65
 * ("The DrawingManager functionality in the Maps JavaScript API is no
 * longer available in the Maps JavaScript API as of version 3.65").
 * We pin to the last channel where it still works so the existing polygon
 * drawers (ServiceAreaMap, PolygonTaxZoneDialog) keep functioning.
 *
 * This is a SHORT-TERM PIN. Follow-up: migrate both drawers to a custom
 * click-based polygon implementation (handle map `click` events, build
 * the ring as the user taps, close on double-click / explicit Done) so
 * we don't need the deprecated library at all. Tracked in NOT-? FE
 * tech-debt.
 *
 * https://developers.google.com/maps/deprecations
 */
export const GOOGLE_MAPS_VERSION = '3.64' as const;
